import { combineReducers } from 'redux';
import { formReducer, modelReducer, actions as formActions } from 'react-redux-form';
import groupBy from '../utils/groupBy';
import {conjunct} from "utils";

export default class CommunicationWorkflowDuck {
  constructor() {
    this.name = 'communicationWorkflow';

    this.defaultModelState = {};
    this.defaultContent = { recipient: {}, content: {}, contact: {} };
    this.defaultIsLoadingPreviewContent = false;

    this.actionType = this.actionType.bind(this);
    this.actions = this.actions.bind(this);
    this.reducer = this.reducer.bind(this);
    this.types = this.types.bind(this);
    this.previewRecipientId = this.previewRecipientId.bind(this);
    this.previewContent = this.previewContent.bind(this);
    this.isLoadingPreviewContent = this.isLoadingPreviewContent.bind(this);
    this.reset = this.reset.bind(this);
    this.page = this.page.bind(this);
    this.setPage = this.setPage.bind(this);
  }

  actionType(action) {
    return `schooladmin/${this.name}/${action}`
  };

  actions() {
    return {
      getTokens: this.getTokens,
      reset: this.reset,
      setPage: this.setPage,
      setPreviewContent: this.setPreviewContent,
      setIsLoadingPreviewContent: this.setIsLoadingPreviewContent,
      setPreviewRecipientId: this.setPreviewRecipientId,
      setSnippet: this.setSnippet,
      setRecipients: this.setRecipients,
      validateSnippet: this.validateSnippet
    }
  };

  reducer() {
    return combineReducers({
      model: modelReducer(`${this.name}.model`, this.defaultModelState),
      form: formReducer(`${this.name}.model`, this.defaultModelState),

      page: this.page,
      previewRecipientId: this.previewRecipientId,
      previewContent: this.previewContent,
      isLoadingPreviewContent: this.isLoadingPreviewContent
    })
  };

  types() {
    return {
      SET_ACTIVE_PREVIEW: this.actionType('SET_ACTIVE_PREVIEW'),
      SET_PAGE: this.actionType('SET_PAGE'),
      SET_PREVIEW_CONTENT: this.actionType('SET_PREVIEW_CONTENT'),
      SET_IS_LOADING_PREVIEW_CONTENT: this.actionType('SET_IS_LOADING_PREVIEW_CONTENT')
    }
  };

  // Reducers

  previewRecipientId(state = '', action) {
    switch (action.type) {
      case this.types().SET_ACTIVE_PREVIEW:
        return action.previewRecipientId;
      default:
        return state;
    }
  };

  previewContent(state = this.defaultContent, action) {
    switch (action.type) {
      case this.types().SET_PREVIEW_CONTENT:
        // Allow for nulling out of current content to reset the state
        return action.previewContent || this.defaultContent;
      default:
        return state;
    }
  };

  isLoadingPreviewContent(state = this.defaultIsLoadingPreviewContent, action) {
    switch (action.type) {
      case this.types().SET_IS_LOADING_PREVIEW_CONTENT:
        return action.isLoadingPreviewContent || this.defaultIsLoadingPreviewContent;
      default:
        return state;
    }
  };

  page(state = 0, action) {
    switch (action.type) {
      case this.types().SET_PAGE:
        return action.page;
      default:
        return state;
    }
  };

  // Action creators

  getTokens() {
    throw 'Override in child class';
  };

  setPreviewRecipientId() {
    throw 'Override in child class';
  };

  reset() {
    return (dispatch) => {
      dispatch(formActions.reset(`${this.name}.model`));
      dispatch(this.setPage(0));
    }
  }

  setPreviewContent() {
    throw 'Override in child class';
  };

  setPage(page) {
    return (dispatch) => {
      dispatch(formActions.setPending(`${this.name}.model`, false));
      dispatch({type: this.types().SET_PAGE, page});
    }
  };

  setPreviewContent(previewContent) {
    return (dispatch) =>
      dispatch({type: this.types().SET_PREVIEW_CONTENT, previewContent})
  };

  setIsLoadingPreviewContent(isLoadingPreviewContent) {
    return (dispatch) =>
      dispatch({ type: this.types().SET_IS_LOADING_PREVIEW_CONTENT, isLoadingPreviewContent })
  };

  setRecipients() {
    throw 'Override in child class';
  };

  setSnippet() {
    throw 'Override in child class';
  };

  validateSnippet() {
    throw 'Override in child class';
  };

  // Helpers

  createRecipientsList(relatedContacts, recipientFilter) {
    const relationshipDescriptors = {
      'parent': 'Parent of',
      'stepparent': 'Parent of',
      'guardian': 'Guardian of',
      'consultant': 'Consultant for'
    };

    let recipients = [];

    for (let contactId in relatedContacts) {
      // noinspection JSUnfilteredForInLoop
      let relationshipDetails = relatedContacts[contactId];
      let contact = relationshipDetails.contact;

      relationshipDetails.related.forEach((relatedContact) => {
        const relationships = [
          {
            ...contact,
            relation_type: relatedContact.relation_type,
          },
          ...(relatedContact.family_contacts || [])
        ];

        const groupedRelationships = groupBy(relationships, 'relation_type');

        const relationTexts = Object.keys(groupedRelationships).map((relation_type) => {
          const related = groupedRelationships[relation_type];

          if (relation_type == 'self') {
            return 'Selected contact';
          }
          else {
            const names = related.map(rel => rel.name || `${rel.first_name} ${rel.last_name}`).sort();

            return `${relationshipDescriptors[relation_type]} ${conjunct(names)}`;
          }
        }).filter(x=>x);

        recipients.push(
          Object.assign({}, relatedContact, {
            relations: relationTexts,
            related_id: contact.id
          })
        );
      });
    }

    recipients = recipientFilter(recipients);

    return recipients;
  };
}
